/**
 * ServiceOrderMovementsTab - Aba Movimentações do modal da OS
 * Lista os itens de estoque (rastreadores, chips e kits) movimentados na
 * conclusão da OS, reutilizando a tabela oficial `StockMovementsTable`
 * (mesmo componente da aba Movimentações dos modais de Kit/Rastreador/Chip).
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-09 v1.1.0 - Renomeada de ServiceOrderProductsTab; passa a
 *            reutilizar StockMovementsTable (compact) com cabeçalho de total +
 *            botão atualizar, no mesmo leiaute do modal de kit/rastreador.
 *   RELEASE: 2026-10-08 v1.0.0 - Criação inicial (listagem própria simples).
 */
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { InlineNotice } from '@/components/ui/inline-notice';
import { StockMovementsTable } from '@/components/stock/StockMovementsTable';
import { cn } from '@/lib/utils';
import { useServiceOrderStockMovements } from '@/hooks/useServiceOrderStock';
import { sortMovementsForDisplay } from '@/lib/stockMovementDisplayOrder';

export function ServiceOrderMovementsTab({ serviceOrderId }: { serviceOrderId: string }) {
  const { data, isLoading, isFetching, error, refetch } = useServiceOrderStockMovements(serviceOrderId, true);
  if (error) return <div className="p-4"><InlineNotice variant="error">{(error as Error).message}</InlineNotice></div>;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Cabeçalho com totalizador e atualização manual (padrão do modal de kit) */}
      <div className="flex shrink-0 items-center justify-between gap-3 border-b py-3">
        <p className="text-sm text-muted-foreground">{data?.length ?? 0} movimentação(ões)</p>
        <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => refetch()} disabled={isFetching} title="Atualizar movimentações" aria-label="Atualizar movimentações">
          <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
        </Button>
      </div>
      {/* Sem padding lateral: as células da tabela já têm espaçamento próprio (padrão das listagens) */}
      <div className="min-h-0 flex-1 overflow-auto py-4">
        {/* Empate de horário (mesma operação): instalação aparece como a mais recente */}
        <StockMovementsTable items={sortMovementsForDisplay(data ?? [])} isLoading={isLoading} compact />
      </div>
    </div>
  );
}
